import { ref, readonly } from 'vue'
import { consola } from 'consola'
import { 
  fetchOrderInfo,
  fetchOrderDetails,
  fetchUnpaidOrdersFromAPI, 
} from '../lib/apiUtils'
import { 
  hasSignificantStatusChange,
  createMinimalOrder,
  updateOrderStatuses,
  logOrderDebugInfo, 
} from '../lib/orderUtils'
import { 
  clearUnpaidOrderFromStorage,
  clearAllOrdersFromStorage,
  saveOrderToUserHistory,
} from '../lib/storageUtils'
import { 
  calculateTokenExpiration, 
} from '../lib/tokenUtils'
import { handleOrderDetailsAPIError } from '../lib/modalUtils'
import { useOrderTime } from './useOrderTime'
import { PAYMENT_TIMEOUT_MINUTES as _PAYMENT_TIMEOUT_MINUTES } from '@shared/config'
import { logOrderTimeInfo, calculateOrderExpiration , convertUtcToMoscowTime } from '@shared/lib'
import type { Order, GetRetryPaymentOrdersResponse } from '@entities/order'
import { useAuthState } from '~/entities/auth'

/**
 * Composable для работы с API заказов
 */
const logger = consola.withTag('order-api')

export function useOrderAPI() {
  const { isLoggedIn } = useAuthState()
  const orderTime = useOrderTime()
  
  // Reactive состояния
  const isLoading = ref(false)
  const isLoadingDetails = ref(false)
  const error = ref<string | null>(null)

  /**
   * Выбирает самый новый неоплаченный заказ из списка
   */
  function selectLatestUnpaidOrder(orders: any[]): any {
    if (orders.length === 1) return orders[0]
    
    // Сортируем по дате создания (самый новый первый)
    const sortedOrders = orders
      .filter(order => 
        order.status === 'pending' && 
        order.payment_status === 'pending',
      )
      .sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return dateB - dateA // Новые первыми
      })
    
    return sortedOrders[0] || orders[0] // Fallback на первый если нет неоплаченных
  }

  /**
   * Быстро обновляет только статус заказа без полной перезагрузки
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- обновление статуса и сравнение полей заказа
  async function updateOrderStatus(
    orderId: number | string,
    currentOrder: Order | null,
    onOrderUpdate: (order: Order) => void,
    onFullReload: (orderId: string | number) => Promise<Order | null>,
  ): Promise<{ status: string; payment_status: string } | null> {
    if (!orderId) return null
    
    logOrderDebugInfo(currentOrder, 'Current order before status update')
    
    try {
      const orderIdStr = String(orderId)
      const statusData = await fetchOrderInfo(orderIdStr) as any
      
      if (!statusData) {
        logger.warn('No status data received from API')
        return null
      }
      
      // Если есть текущий заказ, обновляем только статусы
      if (currentOrder && statusData) {
        const oldStatus = currentOrder.status
        const oldPaymentStatus = (currentOrder as any).payment_status ?? currentOrder.paymentStatus ?? ''
        
        // Проверяем, произошло ли значительное изменение статуса
        const significantChange = hasSignificantStatusChange(
          oldStatus, 
          statusData.status, 
          oldPaymentStatus, 
          statusData.payment_status,
        )
        
        if (significantChange) {
          logger.info('Significant status change detected, performing full reload', {
            orderId,
            oldStatus,
            newStatus: statusData.status,
            oldPaymentStatus,
            newPaymentStatus: statusData.payment_status,
          })
          try {
            const fullOrder = await onFullReload(orderId)
            
            // Сохраняем обновленный заказ в историю пользователя
            if (fullOrder) {
              saveOrderToUserHistory(fullOrder)
            }
            
            return { 
              status: fullOrder?.status || statusData.status, 
              payment_status: ((fullOrder as any)?.payment_status ?? fullOrder?.paymentStatus) || statusData.payment_status, 
            }
          } catch (error_) {
            logger.warn('Full reload after status change failed, falling back to status update', error_)
          }
        }
        
        const updatedOrder = updateOrderStatuses(currentOrder, statusData.status, statusData.payment_status)
        onOrderUpdate(updatedOrder)
        
        // Сохраняем обновленный заказ в историю пользователя
        saveOrderToUserHistory(updatedOrder)
        
        // Если заказ стал оплаченным или завершенным, очищаем данные
        if (statusData.payment_status === 'paid' || 
            ['confirmed', 'preparing', 'ready', 'delivery', 'delivered'].includes(statusData.status)) {
          clearUnpaidOrderFromStorage()
        }
      } else {
        logger.warn('No current order or status data available during update')
        
        // Если нет заказа, но есть statusData и orderId, попробуем загрузить полный заказ
        if (!currentOrder && statusData && orderId) {
          try {
            const fullOrder = await onFullReload(orderId)
            
            // Сохраняем загруженный заказ в историю пользователя
            if (fullOrder) {
              saveOrderToUserHistory(fullOrder)
            }
            
            return { 
              status: fullOrder?.status || statusData.status, 
              payment_status: ((fullOrder as any)?.payment_status ?? fullOrder?.paymentStatus) || statusData.payment_status, 
            }
          } catch {
            logger.warn('Failed to load full order, creating minimal order from status data', { orderId })
            
            const minimalOrder = createMinimalOrder(orderId, statusData.status, statusData.payment_status)
            onOrderUpdate(minimalOrder)
            
            // Сохраняем минимальный заказ в историю пользователя
            saveOrderToUserHistory(minimalOrder)
          }
        }
      }
      
      return statusData
    } catch (error_: any) {
      logger.warn('Failed to update order status (non-critical)', error_)
      return null
    }
  }

  /**
   * Загружает полную информацию о заказе
   */
  async function loadOrderDetails(
    orderId: string | number,
    onOrderUpdate: (order: Order) => void,
  ): Promise<Order | null> {
    if (isLoadingDetails.value) {
      logger.debug?.('Order details request skipped due to ongoing load', { orderId })
      return null
    }
    
    isLoadingDetails.value = true
    error.value = null
    
    try {
      const orderDetails = await fetchOrderDetails(String(orderId))
      
      if (!orderDetails) {
        throw new Error('No order details received from API')
      }
      
      if (!orderDetails.items || orderDetails.items.length === 0) {
        logger.warn('Order details received without items', { orderId })
      }
      
      logOrderDebugInfo(orderDetails, 'Order details from API')
      
      // Преобразуем время в московское перед обновлением
      const orderWithMoscowTime = {
        ...orderDetails,
        createdAt: convertUtcToMoscowTime(orderDetails.createdAt),
        updatedAt: convertUtcToMoscowTime(orderDetails.updatedAt),
        // Добавляем поля с подчеркиваниями для совместимости
        created_at: convertUtcToMoscowTime(orderDetails.createdAt),
        updated_at: convertUtcToMoscowTime(orderDetails.updatedAt),
      }
      
      // Обновляем данные через колбэк
      onOrderUpdate(orderWithMoscowTime)
      isLoadingDetails.value = false
      return orderWithMoscowTime
    } catch (error_: any) {
      error.value = error_.message || 'Ошибка при загрузке деталей заказа'
      logger.error('Failed to load order details', error_)
      handleOrderDetailsAPIError()
      isLoadingDetails.value = false
      return null
    }
  }

  /**
   * Обрабатывает ответ API с неоплаченными заказами
   */
  function processUnpaidOrdersResponse(
    response: GetRetryPaymentOrdersResponse,
    onOrderUpdate: (order: Order) => void,
    onOrderIdUpdate: (orderId: number) => void,
    onTokenExpirationUpdate: (expiration: any) => void,
  ): void {
    if (!response?.success || !response.orders || response.orders.length === 0) {
      logger.info('No unpaid orders found')
      // Не очищаем историю, чистим только массив неоплаченных
      clearUnpaidOrderFromStorage()
      return
    }
    
    // Сортируем по дате создания (новые первыми)
    const sorted = [...response.orders].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    
    // Фильтруем только заказы с действительным временем оплаты (созданные в последние 15 минут)
    const validOrders = sorted.filter(order => {
      const isRecent = orderTime.isOrderRecent(order.createdAt)
      
      // Логируем информацию о времени для каждого заказа
      logOrderTimeInfo(order.id, order.createdAt)
      
      return isRecent
    })
    
    if (validOrders.length === 0) {
      logger.info('No valid orders with active payment time, clearing store')
      clearUnpaidOrderFromStorage()
      return
    }
    
    const latest = selectLatestUnpaidOrder(validOrders as any)
    
    // ✅ УДАЛЕНО: Больше не сохраняем в localStorage
    // Данные управляются через Pinia Store

    // Выставляем в store последний заказ
    if (latest) {
      // Преобразуем время в московское время перед сохранением
      const orderWithMoscowTime = {
        ...latest,
        createdAt: convertUtcToMoscowTime(latest.createdAt),
        updatedAt: convertUtcToMoscowTime(latest.updatedAt),
      }
      
      onOrderUpdate(orderWithMoscowTime)
      onOrderIdUpdate(Number(latest.id))
      if (latest.createdAt) {
        // Используем оригинальное UTC время для расчетов
        const _expiration = calculateOrderExpiration(latest.createdAt)
        const tokenExpiration = calculateTokenExpiration(latest.createdAt)
        onTokenExpirationUpdate(tokenExpiration)
      }
    }
  }

  /**
   * Запрашивает неоплаченные заказы от API
   */
  async function fetchUnpaidOrders(
    onOrderUpdate: (order: Order) => void,
    onOrderIdUpdate: (orderId: number) => void,
    onTokenExpirationUpdate: (expiration: any) => void,
    onReset: () => void,
    userId: string,
  ): Promise<void> {
    // Проверка авторизации
    if (!isLoggedIn.value) {
      logger.warn('User is not authenticated, skipping retry-payment request')
      // Очищаем локальные данные, если есть
      onReset()
      clearAllOrdersFromStorage()
      return
    }
    
    try {
      const response = await fetchUnpaidOrdersFromAPI(userId)
      
      processUnpaidOrdersResponse(response, onOrderUpdate, onOrderIdUpdate, onTokenExpirationUpdate)
    } catch (error_: any) {
      logger.error('Failed to fetch unpaid orders', error_)
      error.value = error_.message || 'Ошибка при получении неоплаченных заказов'
      throw error_
    }
  }

  return {
    // State
    isLoading: readonly(isLoading),
    isLoadingDetails: readonly(isLoadingDetails),
    error: readonly(error),
    
    // Actions
    updateOrderStatus,
    loadOrderDetails,
    fetchUnpaidOrders,
    fetchUnpaidOrdersFromAPI, // ✅ Добавляем прямой экспорт
    processUnpaidOrdersResponse,
  }
}
